import React, { FC, useEffect, useState } from "react";
import { useForm, useFieldArray, FieldError } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InfoIcon from "@mui/icons-material/Info";
import { useParams } from "react-router-dom";
import {
  faChevronRight,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  Box,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  Divider,
  Tooltip,
  Alert,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { ProfileData } from "../profile/profile";
import CardPreview from "../card/card-preview";
import { ToastContainer } from "react-toastify";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { ColourPalette } from "../coloir-picker/colour-palette";
import { useIsMobile } from "../../hooks/useIsMobile";
import { loadStripe } from "@stripe/stripe-js";
import LoadingSpinner from "../loading/loading-spinner";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { useFeatureFlagEnabled } from "posthog-js/react";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

interface CardPreviewProps {
  data?: ProfileData;
  isCheckout?: boolean;
  setCurrentStep?: (step: number) => void;
  currentStep?: number;
}

const Details: FC<CardPreviewProps> = ({
  data,
  isCheckout = true,
  setCurrentStep,
  currentStep
}) => {
  const [isStripeLoaded, setIsStripeLoaded] = useState<boolean>(false);
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState<number>(currentStep || 0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate(); // Initialize the useHistory hook to navigate
  const [userId, setUserId] = useState(() => {
    return localStorage.getItem("userId") || undefined; // Load from storage if available
  });
  const flagEnabled = useFeatureFlagEnabled("new-checkout");

  const stepNames = ["customise_card", "personal_details", "payment"];

  useEffect(() => {
    // Pass activeStep to parent whenever it changes
    setCurrentStep?.(activeStep);
  }, [activeStep, setCurrentStep]);

  useEffect(() => {
    // Check for ttclid in sessionStorage or URL
    const params = new URLSearchParams(window.location.search);
    const ttclidFromUrl = params.get("ttclid");
    const ttclid = ttclidFromUrl || sessionStorage.getItem("ttclid");

    // Persist for future use
    if (ttclidFromUrl) {
      sessionStorage.setItem("ttclid", ttclidFromUrl);
      posthog.register({ ttclid: ttclidFromUrl }); // sets it globally for all events
    }

    const stepName = stepNames[activeStep];
    if (stepName) {
      posthog.capture("checkout_step_viewed", {
        step: stepName,
        ...(ttclid ? { ttclid } : {}),
      });
    }
  }, [activeStep]);

  useEffect(() => {
    stripePromise
      .then((stripeInstance) => {
        if (!stripeInstance) {
          setIsStripeLoaded(false);
        } else {
          setIsStripeLoaded(true);
        }
      })
      .catch(() => {
        setIsStripeLoaded(false);
      });
  }, []);

  const predefinedBorderColors = [
    { name: "Red", value: "#CC0000" },
    { name: "Blue", value: "#0000CC" },
    { name: "Green", value: "#00CC00" },
    { name: "Magenta", value: "#CC00CC" },
    { name: "Yellow", value: "#FFFF00", singleColour: "Black" },
    { name: "Cyan", value: "#00CCCC" },
  ];

  const predefinedTextColors = [
    { name: "White", value: "#FFFFFF" },
    { name: "Black", value: "#000000" },
  ];

  const isMobile = useIsMobile();

  const onSubmit = async (formData: ProfileData) => {
    setError(null);
    setIsLoading(true);

    try {
      if (isCheckout) {
        // --- CHECKOUT MODE: create user (POST) ---
        const checkoutBody: ProfileData = {
          ...formData,
          id: userId,
        };

        // Optionally clean emergency contacts and insulin types
        if (
          formData.emergencyContacts?.some(
            (e) => !e.name || !e.phone
          )
        ) {
          delete checkoutBody.emergencyContacts;
        }

        if (
          formData.insulinTypes?.some(
            (i) => !i.type || !i.dosage
          )
        ) {
          delete checkoutBody.insulinTypes;
        }
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/users`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(checkoutBody),
          }
        );

        const data = await response.json();

        if (data.userId) {
          setUserId(data.userId);
          setActiveStep(2);
        }
      } else {
        // --- PROFILE EDIT MODE: update user (PUT) ---

        const updateBody: ProfileData = {
          ...formData,
        };

        // Optionally clean emergency contacts and insulin types
        // if (
        //   formData.emergencyContacts?.some(
        //     (e) => !e.name || !e.phone
        //   )
        // ) {
        //   delete updateBody.emergencyContacts;
        // }

        // if (
        //   formData.insulinTypes?.some(
        //     (i) => !i.type || !i.dosage
        //   )
        // ) {
        //   delete updateBody.insulinTypes;
        // }

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/users/${id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updateBody),
          }
        );

        const data = await response.json();

        if (response.status === 200) {
          navigate(`/profile/${id}`, {
            state: {
              ...data,
              id,
              toastMessage: "Profile updated successfully!",
            },
          });
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred. If the error persists, please contact us."
      );
    } finally {
      window.scrollTo(0, 0);
      setIsLoading(false);
    }
  };

  const fetchClientSecret = async () => {
    // Create a Checkout Session
    localStorage.setItem("userId", userId || "");
    const res = await fetch(
      `${import.meta.env.VITE_API_URL}/api/create-checkout-session`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, email: watchAllFields.email }),
      }
    );
    const data = await res.json();

    return data.clientSecret;
  };

  const options = { fetchClientSecret };

  const {
    register,
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
    watch,
  } = useForm<ProfileData>({
    mode: "onChange",
    defaultValues: sessionStorage.getItem("formData")
      ? JSON.parse(sessionStorage.getItem("formData")!)
      : {
        emergencyContacts: [{ name: "", phone: "" }],
        insulinTypes: [{ type: "", dosage: "" }],
        meta: {
          cardBorderColour: "#0000FF",
          cardTextColour: "#FFFFFF",
        },
        ...data,
      },
  });
  const borderColour = watch("meta.cardBorderColour");
  const textColour = watch("meta.cardTextColour");
  const displayName = watch("name");
  const displayDateOfBirth = watch("dateOfBirth");
  const watchAllFields = watch();
  useEffect(() => {
    sessionStorage.setItem("formData", JSON.stringify(watchAllFields));
  }, [watchAllFields]);
  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({ control, name: "emergencyContacts" });

  const {
    fields: insulinFields,
    append: appendInsulin,
    remove: removeInsulin,
  } = useFieldArray({ control, name: "insulinTypes" });
  const handleNext = async (e: React.MouseEvent) => {
    e.preventDefault();
    let fieldsToValidate: (keyof ProfileData)[] = [];

    switch (activeStep) {
      case 1:
        fieldsToValidate = [
          "name",
          "email",
          "dateOfBirth",
          // "emergencyContacts",
          // "insulinTypes",
          // "emergencyInstructions",
          "termsAccepted",
        ];
        break;
    }
    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  const steps = ["Customise", "Details", "Payment"];
  return isLoading ? (
    <div className="mx-auto p-2">
      <LoadingSpinner />
    </div>
  ) : (
    <div className="mx-auto p-2">
      <ToastContainer />
      {isCheckout && !isMobile && (
        <Stepper activeStep={activeStep} className="mb-8">
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>
                <Typography fontSize="1.4rem">{label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      <Box>
        <Paper className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {activeStep === 0 && (
              <Box className="flex flex-col gap-4 items-center relative">
                {/* Card + Button */}
                <div className="flex flex-col gap-6 items-center">
                  <CardPreview
                    fullName={displayName || "Test User"}
                    dateOfBirth={displayDateOfBirth || "12/01/1990"}
                    borderColour={borderColour}
                    textColour={
                      predefinedBorderColors
                        .find((c) => c.value === borderColour)
                        ?.singleColour?.toLowerCase() ?? textColour
                    }
                    diabetesTextColour={
                      predefinedBorderColors
                        .find((c) => c.value === borderColour)
                        ?.singleColour?.toLowerCase() ?? "#000000"
                    }
                  />
                </div>
                <p className="italic text-xs max-w-[320px]">
                  Card preview for reference. Final product may vary slightly.
                  For longer names, this will be adjusted accordingly in the
                  final print.
                </p>
                {/* Color Pickers */}
                <ColourPalette
                  setValue={setValue}
                  watch={watch}
                  predefinedBorderColors={predefinedBorderColors}
                  predefinedTextColors={predefinedTextColors}
                />
                <Alert severity="warning">
                  <p className="text-xl md:text-sm">
                    If you require multiple cards with different details, please
                    complete your first purchase and return to checkout to
                    complete any further purchases.
                  </p>
                </Alert>
              </Box>
            )}

            {activeStep === 1 && (
              <Box className="space-y-6 md:space-y-4">
                <p className="mb-4 text-2xl">Personal Details</p>
                <TextField
                  fullWidth
                  label="Full Name"
                  {...register("name", { required: "Name is required" })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
                {isCheckout && (
                  <TextField
                    fullWidth
                    label="Email"
                    {...register("email", { required: "Email is required" })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  {...register("dateOfBirth", {
                    required: "Date of birth is required",
                    validate: (value) => {
                      const selectedDate = new Date(value);
                      const minDate = new Date("1900-01-01");
                      const maxDate = new Date();

                      if (selectedDate < minDate) {
                        return "Date of birth cannot be before 1900";
                      }
                      if (selectedDate > maxDate) {
                        return "Date of birth cannot be in the future";
                      }

                      return true;
                    },
                  })}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      min: "1900-01-01",
                      max: new Date().toISOString().split("T")[0],
                    },
                  }}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth?.message}
                />
                <Divider className="pt-4" />
                <p className="text-3xl">Emergency Information</p>
                <p className="text-md">All the information below is <span className="font-bold text-red-400">optional</span> and can be added at a later date. However, we do encourage adding as much information as you can to aid those who find you in an emergency.</p>
                {isMobile && <Divider className="pt-4" />}
                <p className="text-2xl">Emergency Contacts</p>
                <p className="md:text-sm text-xl">
                  You have the option to enable SMS notifications for each of
                  your emergency contacts, ensuring they are alerted whenever
                  your QR code is scanned.
                </p>
                {contactFields.map((field, index) => (
                  <Box
                    key={field.id}
                    className="flex flex-col md:bg-white bg-gray-50 gap-4 w-full md:border-none border border-gray-300 md:p-0 p-5 rounded-lg md:shadow-none shadow-sm"
                  >
                    <div className="flex md:flex-row flex-col gap-4 md:gap-4">
                      <TextField
                        className="flex-1 bg-white"
                        label="Emergency Contact Name"
                        {...register(
                          `emergencyContacts.${index}.name` as const
                        )}
                        error={!!errors.emergencyContacts?.[index]?.name}
                        helperText={
                          errors.emergencyContacts?.[index]?.name?.message
                        }
                      />
                      <TextField
                        className="flex-1 bg-white"
                        label="Phone Number"
                        {...register(
                          `emergencyContacts.${index}.phone` as const
                        )}
                        error={!!errors.emergencyContacts?.[index]?.phone}
                        helperText={
                          errors.emergencyContacts?.[index]?.phone?.message
                        }
                      />
                      {contactFields.length > 1 && !isMobile && (
                        <IconButton
                          onClick={() => removeContact(index)}
                          color="error"
                          className="w-fit"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </div>
                    <Box className="flex items-center ml-2 justify-start flex-row md:gap-6 gap-12 md:text-lg text-xl">
                      <label className="flex items-center gap-4">
                        <input
                          type="checkbox"
                          className="md:w-3 w-6 md:h-3 h-6  accent-blue-600" // Adjust size and color
                          {...register(`emergencyContacts.${index}.notifySMS`)}
                        />
                        <span>Notify via SMS</span>
                      </label>
                      <Tooltip
                        title="By ticking this box, your emergency contact will receive notifications anytime the QR code is scanned."
                        arrow
                      >
                        <InfoIcon color="action" />
                      </Tooltip>
                    </Box>
                    {contactFields.length > 1 && isMobile && (
                      <Button
                        aria-label={`Remove emergency contact ${index}`}
                        onClick={() => removeContact(index)}
                        variant="contained"
                        color="error"
                        className="w-full"
                      >
                        <div className="flex flex-row gap-3 items-center">
                          <DeleteIcon />
                          <p className="text-md">Remove Emergency Contact</p>
                        </div>
                      </Button>
                    )}
                  </Box>
                ))}
                <Button
                  aria-label="Add emergency contact"
                  className="flex flex-row md:gap-1 gap-3"
                  onClick={() => appendContact({ name: "", phone: "" })}
                >
                  <AddIcon />
                  <p className="md:text-sm text-start text-xl">
                    Add Emergency Contact
                  </p>
                </Button>
                <Divider className="pt-4 md:pt-0" />
                <p className="text-2xl">Insulin Information</p>
                {insulinFields.map((field, index) => (
                  <Box
                    key={field.id}
                    className="flex flex-col md:bg-white bg-gray-50 gap-4 w-full md:border-none border border-gray-300 md:p-0 p-5 rounded-lg md:shadow-none shadow-sm"
                  >
                    <div className="flex md:flex-row flex-col gap-4 md:gap-4">
                      <TextField
                        className="flex-1 bg-white"
                        label="Insulin Type"
                        {...register(`insulinTypes.${index}.type`)}
                        error={!!errors.insulinTypes?.[index]?.type}
                        helperText={
                          (errors.insulinTypes?.[index]?.type as FieldError)
                            ?.message
                        }
                      />

                      <TextField
                        className="flex-1 bg-white"
                        label="Dosage"
                        {...register(`insulinTypes.${index}.dosage` as const)}
                        {...(isMobile && {
                          slotProps: { inputLabel: { shrink: true } },
                        })}
                        error={!!errors.insulinTypes?.[index]?.dosage}
                        helperText={
                          errors.insulinTypes?.[index]?.dosage?.message
                        }
                      />

                      {insulinFields.length > 1 &&
                        (isMobile ? (
                          <Button
                            aria-label={`Remove insulin entry ${index}`}
                            onClick={() => removeInsulin(index)}
                            variant="contained"
                            color="error"
                            className="w-full"
                          >
                            <div className="flex flex-row gap-3 items-center">
                              <DeleteIcon />
                              <p className="text-md">Remove Insulin Entry</p>
                            </div>
                          </Button>
                        ) : (
                          <IconButton
                            onClick={() => removeInsulin(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        ))}
                    </div>
                  </Box>
                ))}
                <Button
                  aria-label="Add insulin type"
                  className="flex flex-row md:gap-1 gap-3"
                  onClick={() => appendInsulin({ type: "", dosage: "" })}
                >
                  <AddIcon />
                  <p className="md:text-sm text-xl">Add Insulin Type</p>
                </Button>
                <TextField
                  fullWidth
                  multiline
                  placeholder=""
                  rows={5}
                  label="Instructions for Emergency Services"
                  {...register("emergencyInstructions")}
                  error={!!errors.emergencyInstructions}
                  helperText={errors.emergencyInstructions?.message}
                />
                {isCheckout && (
                  <>
                    <Alert severity="info">
                      Please review and accept the terms before proceeding.
                    </Alert>
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-[auto,1fr] gap-4 items-start">
                        <input
                          type="checkbox"
                          className="w-5 h-5"
                          {...register("termsAccepted", {
                            required: true,
                          })}
                          checked={watch("termsAccepted") || false} // Ensure explicit boolean value
                        />
                        <span className="text-lg -mt-1">
                          I am a UK resident and I agree to the{" "}
                          <a
                            href="/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            Terms & Conditions
                          </a>{" "}
                          and{" "}
                          <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            Privacy Policy
                          </a>
                        </span>
                      </div>
                      <div className="grid grid-cols-[auto,1fr] gap-4 items-start">
                        <input
                          type="checkbox"
                          className="w-5 h-5"
                          {...register("allowMarketing", {
                            required: false,
                          })}
                          checked={watch("allowMarketing") || false}
                        />
                        <span className="text-lg -mt-1">
                          Would you like the occasional update about new
                          products and important news?
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </Box>
            )}

            {activeStep === 2 && (
              <>
                {flagEnabled && (
                  <Alert severity="info" className="mb-4">
                    <p className="text-2xl font-semibold -mt-[5px]">
                      You're almost done!
                    </p>
                    <p className="text-lg">
                      This is a one-time payment of <strong>£14.99</strong> for
                      your Diamedic emergency card.
                    </p>
                    <p className="text-lg mt-4">
                      You'll receive a purchase confirmation email after your
                      payment is complete. Your card will then be dispatched
                      within 2 working days.
                    </p>
                  </Alert>
                )}
                <Box className="flex justify-center w-full">
                  <Box className="w-full max-w-[500px] space-y-4">
                    {isStripeLoaded ? (
                      <div id="checkout">
                        <p className="text-center text-xl mb-4">
                          🔒 Secure Checkout (Powered by Stripe)
                        </p>
                        <EmbeddedCheckoutProvider
                          stripe={stripePromise}
                          options={options}
                        >
                          <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                      </div>
                    ) : (
                      <div>
                        Unable to load Stripe checkout. Please refresh the page,
                        and if the problem persists contact support.
                      </div>
                    )}
                  </Box>
                </Box>
              </>
            )}

            <Box
              className={`flex md:flex-row flex-col ${activeStep > 0 ? "justify-between" : "justify-end"
                } gap-4 md:pt-4 pt-4`}
            >
              {activeStep > 0 && isCheckout && (
                <Button
                  aria-label="Back"
                  className="md:h-[60px] h-[70px] w-full md:w-[200px] rounded-full transition duration-300 ease-in-out transform"
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    handleBack();
                    window.scrollTo(0, 0);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  <p className="text-3xl md:text-xl">Back</p>
                  <FontAwesomeIcon
                    icon={faChevronLeft}
                    size="2xl"
                    className="absolute left-4"
                  />
                </Button>
              )}
              {activeStep < steps.length - 2 && isCheckout && (
                <Button
                  aria-label="Next"
                  className="md:h-[60px] h-[70px] w-full md:w-[200px] rounded-full transition duration-300 ease-in-out transform"
                  type="button"
                  variant="contained"
                  onClick={(e) => {
                    handleNext(e);
                    window.scrollTo(0, 0);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  <p className="text-3xl md:text-xl">Next</p>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    size="2xl"
                    className="absolute right-4"
                  />
                </Button>
              )}
              {activeStep === 1 && isCheckout && (
                <Button
                  aria-label="Proceed to Payment"
                  className="md:h-[60px] h-[80px] w-full md:w-fit rounded-full transition duration-300 ease-in-out transform"
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: "none" }}
                  disabled={!watch("termsAccepted")}
                >
                  <p className="text-3xl md:text-xl">{"Proceed to Payment"}</p>
                </Button>
              )}
              {!isCheckout && (
                <Button
                  aria-label="Proceed to Payment"
                  className="md:h-[60px] h-[80px] w-full md:w-fit rounded-full transition duration-300 ease-in-out transform"
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: "none" }}
                >
                  <p className="text-3xl md:text-xl">{"Save"}</p>
                </Button>
              )}
            </Box>
          </form>
          {error && (
            <div className="flex justify-center mt-4">
              <p className="text-red-500 text-xl">{error}</p>
            </div>
          )}
        </Paper>
      </Box>
    </div>
  );
};

export default Details;
